import { z } from "zod";
import { narrativeBookProductSchema } from "./narrative-book-production.js";
import { narrativeMediaTruthClassSchema, narrativePageArchetypeSchema, } from "./narrative-visual-production.js";
import { opaqueIdSchema } from "./opaque.js";
const checksumSchema = z.string().regex(/^[a-f0-9]{64}$/u);
export const narrativeMediaAssetSchema = z
    .object({
    assetId: z.string().trim().min(1).max(200),
    bookId: opaqueIdSchema,
    product: narrativeBookProductSchema,
    title: z.string().trim().min(1).max(200),
    truthClass: narrativeMediaTruthClassSchema,
    source: z
        .object({
        sourceId: z.string().trim().min(1).max(200),
        revisionId: z.string().trim().min(1).max(200),
        parentRevisionId: z.string().trim().min(1).max(200).nullable(),
        checksumSha256: checksumSchema,
        originalFilename: z.string().trim().min(1).max(255),
        mimeType: z.enum(["image/jpeg", "image/png", "image/tiff", "application/pdf"]),
        byteSize: z.number().int().positive(),
        pixelWidth: z.number().int().positive().nullable(),
        pixelHeight: z.number().int().positive().nullable(),
        capturedAt: z.string().datetime(),
        synthetic: z.boolean(),
    })
        .strict(),
    rights: z
        .object({
        basis: z.enum([
            "OWNER_CREATED",
            "OWNER_POSSESSES_AND_ATTESTS_RIGHTS",
            "LICENSED_FOR_BOOK",
            "PUBLIC_DOMAIN_VERIFIED",
            "SYNTHETIC_FIXTURE_ONLY",
        ]),
        ownerPersonIds: z.array(opaqueIdSchema).min(1).max(50),
        scope: z.enum(["THIS_EDITION_ONLY", "THIS_BOOK_ALL_EDITIONS", "FIXTURE_ONLY"]),
        attestedAt: z.string().datetime().nullable(),
        withdrawnAt: z.string().datetime().nullable(),
    })
        .strict(),
    depictedPeople: z.array(z
        .object({
        personId: opaqueIdSchema,
        consent: z.enum(["PENDING", "APPROVED", "DECLINED", "NOT_REQUIRED_DECEASED"]),
        decidedAt: z.string().datetime().nullable(),
    })
        .strict()),
    approval: z
        .object({
        requiresJointApproval: z.boolean(),
        principalApprovals: z.array(z
            .object({
            personId: opaqueIdSchema,
            decision: z.enum(["PENDING", "APPROVED", "DECLINED"]),
            decidedAt: z.string().datetime().nullable(),
        })
            .strict()),
        editorDecision: z.enum(["PENDING", "APPROVED", "REJECTED"]),
        approvedAt: z.string().datetime().nullable(),
    })
        .strict(),
    editorial: z
        .object({
        caption: z.string().trim().min(1).max(1_000),
        alternateDescription: z.string().trim().min(1).max(1_000),
        sourceNote: z.string().trim().min(1).max(1_000),
        eventIds: z.array(z.string().trim().min(1).max(200)).max(50),
        claimIds: z.array(z.string().trim().min(1).max(200)).max(200),
    })
        .strict(),
    placement: z
        .object({
        pageArchetype: narrativePageArchetypeSchema,
        orientation: z.enum(["PORTRAIT", "LANDSCAPE", "SQUARE", "OBJECT"]),
        focalPoint: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict(),
        cropApproved: z.boolean(),
        maximumPlacedWidthInches: z.number().positive(),
        maximumPlacedHeightInches: z.number().positive(),
    })
        .strict(),
    quality: z
        .object({
        effectiveDpi: z.number().positive().nullable(),
        orientationNormalized: z.boolean(),
        colorSpace: z.enum(["SRGB", "ADOBE_RGB", "DISPLAY_P3", "GRAY", "UNKNOWN"]),
        missingGlyphRisk: z.literal(false),
    })
        .strict(),
    lineage: z
        .object({
        replacesAssetId: z.string().trim().min(1).max(200).nullable(),
        restorationOfAssetId: z.string().trim().min(1).max(200).nullable(),
        outputChecksumSha256: checksumSchema.nullable(),
    })
        .strict(),
    status: z.enum([
        "PROOF_ONLY",
        "AWAITING_RIGHTS",
        "AWAITING_CONSENT",
        "AWAITING_EDITOR",
        "APPROVED_FOR_BOOK",
        "WITHDRAWN",
        "REJECTED",
    ]),
})
    .strict()
    .superRefine((value, context) => {
    if (value.truthClass.startsWith("DOCUMENTARY") && value.source.synthetic)
        context.addIssue({
            code: "custom",
            path: ["source", "synthetic"],
            message: "Documentary media cannot use a synthetic source asset.",
        });
    if (value.rights.withdrawnAt && value.status !== "WITHDRAWN")
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A withdrawn rights grant requires WITHDRAWN status.",
        });
    if (value.lineage.restorationOfAssetId && value.truthClass !== "DOCUMENTARY_RESTORATION")
        context.addIssue({
            code: "custom",
            path: ["lineage", "restorationOfAssetId"],
            message: "Only documentary restorations may name a restoration source.",
        });
    if (value.status === "APPROVED_FOR_BOOK") {
        if (value.rights.basis === "SYNTHETIC_FIXTURE_ONLY" || !value.rights.attestedAt)
            context.addIssue({
                code: "custom",
                path: ["rights"],
                message: "Book approval requires active, attested non-fixture rights.",
            });
        if (value.quality.effectiveDpi === null || value.quality.effectiveDpi < 300)
            context.addIssue({
                code: "custom",
                path: ["quality", "effectiveDpi"],
                message: "Book approval requires at least 300 effective DPI.",
            });
        if (!value.placement.cropApproved || !value.quality.orientationNormalized)
            context.addIssue({
                code: "custom",
                path: ["placement"],
                message: "Book approval requires normalized orientation and an approved crop.",
            });
        if (value.depictedPeople.some(({ consent }) => consent === "PENDING" || consent === "DECLINED"))
            context.addIssue({
                code: "custom",
                path: ["depictedPeople"],
                message: "Every depicted person must have an allowed consent state.",
            });
        if (value.approval.editorDecision !== "APPROVED" ||
            !value.approval.approvedAt ||
            value.approval.principalApprovals.some(({ decision }) => decision !== "APPROVED"))
            context.addIssue({
                code: "custom",
                path: ["approval"],
                message: "Book approval requires editor and all required principal approvals.",
            });
        if (value.approval.requiresJointApproval && value.approval.principalApprovals.length < 2)
            context.addIssue({
                code: "custom",
                path: ["approval", "principalApprovals"],
                message: "Joint media requires at least two principal decisions.",
            });
    }
});
export const narrativeMediaInventorySchema = z
    .object({
    contract: z.literal("narrative-media-inventory/1"),
    product: narrativeBookProductSchema,
    bookId: opaqueIdSchema,
    minimumApprovedAssets: z.number().int().nonnegative(),
    assets: z.array(narrativeMediaAssetSchema),
})
    .strict();
export const narrativeMediaReadinessSchema = z
    .object({
    contract: z.literal("narrative-media-readiness/1"),
    product: narrativeBookProductSchema,
    status: z.enum(["PASS", "BLOCKED"]),
    totalAssets: z.number().int().nonnegative(),
    approvedAssets: z.number().int().nonnegative(),
    requiredApprovedAssets: z.number().int().nonnegative(),
    blockers: z.array(z
        .object({
        assetId: z.string().trim().min(1).max(200).nullable(),
        code: z.enum([
            "APPROVED_COUNT_BELOW_TARGET",
            "RIGHTS_INCOMPLETE",
            "CONSENT_INCOMPLETE",
            "JOINT_APPROVAL_INCOMPLETE",
            "PRINT_RESOLUTION_INCOMPLETE",
            "ACCESSIBLE_DESCRIPTION_INCOMPLETE",
            "PROOF_ONLY_ASSET",
            "WITHDRAWN_ASSET",
        ]),
        detail: z.string().trim().min(1).max(1_000),
    })
        .strict()),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.status === "PASS") !== (value.blockers.length === 0))
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Media readiness status must agree with blockers.",
        });
    if (value.approvedAssets > value.totalAssets)
        context.addIssue({
            code: "custom",
            path: ["approvedAssets"],
            message: "Approved asset count cannot exceed inventory size.",
        });
});
export const narrativeRepresentativeProofPlanSchema = z
    .object({
    contract: z.literal("narrative-representative-proof-plan/1"),
    product: narrativeBookProductSchema,
    status: z.enum(["COMPOSITION_PASS_MEDIA_BLOCKED", "READY_FOR_OWNER_REVIEW", "APPROVED"]),
    requiredProofs: z
        .array(z
        .object({
        proofId: z.string().trim().min(1).max(200),
        role: z.enum([
            "COVER",
            "CHAPTER_THRESHOLD",
            "ORDINARY_PROSE",
            "DOCUMENTARY_MEDIA",
            "ARTIFACT",
            "ENDING",
            "PAIRED_RECOLLECTION",
            "JOINT_REFLECTION",
        ]),
        pdfPageIndex: z.number().int().nonnegative().nullable(),
        assetIds: z.array(z.string().trim().min(1).max(200)),
        compositionReview: z.enum(["PASS", "FAIL", "PENDING"]),
        mediaAuthenticityReview: z.enum(["PASS", "BLOCKED", "PENDING"]),
        ownerReview: z.enum(["APPROVED", "CHANGES_REQUESTED", "PENDING"]),
    })
        .strict())
        .min(6),
    providerDispatchAllowed: z.literal(false),
    physicalProofRequiredAfterApproval: z.literal(true),
})
    .strict();
//# sourceMappingURL=narrative-media-production.js.map